/**
 * Clients Table Migration Instructions
 * 
 * Since Supabase doesn't allow direct DDL operations from the client,
 * you need to run the migration SQL in the Supabase SQL Editor.
 * 
 * Steps:
 * 1. Go to your Supabase project dashboard
 * 2. Navigate to the SQL Editor
 * 3. Copy and run the migration SQL from DATABASE_MIGRATION_CLIENTS_TABLE.sql
 * 4. Verify the migration using VERIFY_CLIENTS_TABLE_SCHEMA.sql
 */

import { supabase } from '../lib/supabase';

/**
 * Check if the clients table has all required columns
 */
export async function checkClientsTableSchema() {
  console.log('Checking clients table schema...');
  
  try {
    // Try to query with all expected columns
    const { error } = await supabase
      .from('clients')
      .select(`
        id,
        name,
        client_status,
        services_provided,
        lead_source,
        address,
        client_of
      `)
      .limit(1);
    
    if (error) {
      // Parse error to identify missing columns
      const errorMessage = error.message.toLowerCase();
      const missingColumns = [];
      
      const columns = ['client_status', 'services_provided', 'lead_source', 'address', 'client_of'];
      
      columns.forEach(col => {
        if (errorMessage.includes(`column "${col}" does not exist`) || 
            errorMessage.includes(`column ${col} does not exist`)) {
          missingColumns.push(col);
        }
      });
      
      if (missingColumns.length > 0) {
        return {
          valid: false,
          missingColumns,
          message: `Missing columns: ${missingColumns.join(', ')}. Please run the database migration.`
        };
      }
      
      // If it's a different error, throw it
      throw error;
    }
    
    return {
      valid: true,
      message: 'All required columns exist in the clients table'
    };
    
  } catch (error) {
    console.error('Error checking clients table schema:', error);
    return {
      valid: false,
      error: error.message,
      message: 'Unable to verify clients table schema. Please check the database.'
    };
  }
}

/**
 * Get migration SQL for easy copying
 */
export function getMigrationSQL() {
  return `
-- Database Migration: Add missing columns to clients table

-- Add client_status column
ALTER TABLE clients 
ADD COLUMN IF NOT EXISTS client_status VARCHAR(20) DEFAULT 'active';

-- Add services_provided column
ALTER TABLE clients 
ADD COLUMN IF NOT EXISTS services_provided TEXT;

-- Add lead_source column
ALTER TABLE clients 
ADD COLUMN IF NOT EXISTS lead_source VARCHAR(100);

-- Add address column
ALTER TABLE clients 
ADD COLUMN IF NOT EXISTS address TEXT;

-- Add client_of column
ALTER TABLE clients 
ADD COLUMN IF NOT EXISTS client_of VARCHAR(255);

-- Update existing records to have default status
UPDATE clients 
SET client_status = 'active' 
WHERE client_status IS NULL;
  `.trim();
}

/**
 * Display migration instructions in the console
 */
export function showMigrationInstructions() {
  console.log(`
====================================
CLIENTS TABLE MIGRATION REQUIRED
====================================

The clients table is missing required columns.

To fix this:

1. Go to your Supabase project dashboard
2. Click on "SQL Editor" in the left sidebar
3. Create a new query
4. Copy and paste the following SQL:

${getMigrationSQL()}

5. Click "Run" to execute the migration

6. Verify the migration succeeded by running:
   SELECT * FROM clients LIMIT 1;

====================================
  `);
}

// Auto-check on module import
checkClientsTableSchema().then(result => {
  if (!result.valid) {
    console.error('❌ Clients table schema is invalid:', result.message);
    if (result.missingColumns) {
      console.error('Missing columns:', result.missingColumns);
    }
    showMigrationInstructions();
  } else {
    console.log('✅ Clients table schema is valid');
  }
});